import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import "../../assets/styles/custom-styles.css";
import {
  faEdit,
  faTrash,
  faEye,
  faMapMarkerAlt,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { usePermissions } from "../../hooks/usePermissions";
import useLoader from "../../hooks/useLoader";
import useNotification from "../../hooks/useNotification";
import { detailsOrganizationFields } from "../../constants/forms/organizationFields";
import DetailsSectionRenderer from "../../components/DetailsSectionRenderer";
import useForm from "../../hooks/useForm";
import { setDefaultFieldValues } from "../../utils/objectUtils";
import useBaseService from "../../hooks/services/useBaseService";
import { entities } from "../../constants/entities";
import PageHeader from "../../components/PageHeader";
import ListHeader from "../../components/ListHeader";
import useAction from "../../hooks/useAction";
import { Box } from "@mui/material";
import TableWithInactiveFilter from "../../components/table/TableWithInactiveFilter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useGenericFilter from "../../hooks/filters/useGenericFilter";
import useFilterHandlers from "../../hooks/useFilterHandlers";
import useUrlFilters from "../../hooks/useUrlFilters";
import useQueryWithLoader from "../../hooks/useQueryWithLoader";

const OrganizationDetailsPage = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { canAccess } = usePermissions();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const {
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      zip: [],
      street: [],
      deleted_at: false,
    },
    "addressesOrganization",
  );

  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    del: baseServiceDel,
  } = useBaseService(navigate);

  const { formData, setFormData } = useForm(
    setDefaultFieldValues(detailsOrganizationFields),
  );

  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  const { localFilterValues } = useGenericFilter(
    {
      zip: [],
      street: [],
      deleted_at: false,
    },
    handleFilterApply,
    handleFilterClear,
  );

  const {
    data: organizationData,
    isLoading: isLoadingOrganization,
    isError: isErrorOrganization,
    error: organizationQueryError,
  } = useQueryWithLoader({
    queryKey: ["organization", organizationId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.organizations.getById(organizationId),
      );
      return response.result;
    },
    enabled: !!organizationId,
  });

  useEffect(() => {
    if (!isLoadingOrganization && organizationData) {
      hideLoader();
      setFormData({
        name: organizationData.name,
        active: organizationData.active ? "Sim" : "Não",
      });
    }
    if (isErrorOrganization && organizationQueryError) {
      hideLoader();
      console.error("Erro ao buscar organização:", organizationQueryError);
      showNotification(
        "error",
        `Erro ao buscar organização: ${organizationQueryError?.message}`,
      );
      navigate("/aluguel/organizacoes", { replace: true });
    }
  }, [
    isErrorOrganization,
    organizationData,
    isLoadingOrganization,
    organizationQueryError,
    hideLoader,
    setFormData,
    showNotification,
    navigate,
  ]);

  const columns = [
    { key: "alias", label: "Apelido" },
    { key: "street", label: "Rua" },
    { key: "zip", label: "CEP" },
  ];

  const {
    data: addressesOrganizationData,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
  } = useQueryWithLoader({
    queryKey: ["addressesOrganization", organizationId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.organizations.addresses.get(organizationId),
      );
      return response.result.data;
    },
    enabled: !!organizationId,
    select: (data) => {
      return {
        addresses: data.map((address) => ({
          id: address.id,
          alias: address.alias,
          zip: address.zip,
          street: address.formatted_address,
          isDeleted: !!address.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
  });

  const { addresses = [], totalPages = 1 } = addressesOrganizationData || {};

  const inactiveMutation = useMutation({
    mutationFn: async (osDepartamentId) => {
      showLoader();
      const response = await baseServiceDel(
        entities.organizations.addresses.delete(osDepartamentId),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        queryClient.invalidateQueries({
          queryKey: ["addressesOrganization", organizationId],
        });
      }
      hideLoader();
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const activateMutation = inactiveMutation;

  const actions = useMemo(
    () => [
      {
        id: "viewLocations",
        icon: faMapMarkerAlt,
        title: "Ver Localizações",
        buttonClass: "btn-warning",
        permission: "Ver endereços de organizações",
        onClick: (address) =>
          navigate(
            `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/${address.id}/localizacoes`,
          ),
        show: (address) =>
          !address.isDeleted && canAccess("Ver endereços de organizações"),
      },
      {
        id: "viewDetails",
        icon: faEye,
        title: "Ver Detalhes",
        buttonClass: "btn-info",
        permission: "Ver endereços de organizações",
        onClick: (address) =>
          navigate(
            `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/detalhes/${address.id}`,
          ),
        show: (address) =>
          !address.isDeleted && canAccess("Ver endereços de organizações"),
      },
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar endereços de organizações",
        onClick: (address) =>
          navigate(
            `/aluguel/organizacoes/detalhes/${organizationId}/enderecos/editar/${address.id}`,
          ),
        show: (address) =>
          !address.isDeleted &&
          canAccess("Atualizar endereços de organizações"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir endereços de organizações",
        onClick: (address) =>
          showConfirmation(
            address,
            "Você tem certeza que deseja excluir?",
            async () => {
              try {
                await inactiveMutation.mutateAsync(address.id);
              } catch (error) {
                console.log("Erro ao excluir endereço:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (address) =>
          !address.isDeleted && canAccess("Excluir endereços de organizações"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-success",
        permission: "Excluir endereços de organizações",
        onClick: (address) =>
          showConfirmation(
            address,
            "Você tem certeza que deseja restaurar?",
            async () => {
              try {
                await activateMutation.mutateAsync(address.id);
              } catch (error) {
                console.log("Erro ao restaurar endereço:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (address) =>
          address.isDeleted && canAccess("Excluir endereços de organizações"),
      },
    ],
    [
      navigate,
      organizationId,
      canAccess,
      showConfirmation,
      inactiveMutation,
      activateMutation,
    ],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes da Organização"
        showBackButton={true}
        backUrl="/aluguel/organizacoes/dashboard"
      />

      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          sections={detailsOrganizationFields}
          formData={formData}
          readOnly={true}
        />

        <ListHeader
          title="Endereços da Organização"
          buttonText="Adicionar Endereço"
          buttonLink={`/aluguel/organizacoes/detalhes/${organizationId}/enderecos/adicionar`}
          canAccess={canAccess}
          permission="Criar endereços de organizações"
        />

        <Box sx={{ mt: 2 }}>
          <TableWithInactiveFilter
            data={addresses}
            columns={columns}
            actions={actions}
            totalPages={totalPages}
            currentPage={pagination.page - 1}
            onPageChange={changePage}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={(event) => {
              const newPerPage = +event.target.value;
              setPagination({ page: 1, perPage: newPerPage });
            }}
            filterValues={localFilterValues}
            localFilterValues={localFilterValues}
            applyUrlFilters={applyUrlFilters}
            showFilterInactive={false}
            type="default"
            isLoading={isLoadingAddresses}
            isFetching={isFetchingAddresses}
          />
        </Box>
      </div>

      <ConfirmationModal
        open={openModalConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmAction}
        itemName={selectedItem ? selectedItem.street : ""}
        text={action.text}
      />
    </MainLayout>
  );
};

export default OrganizationDetailsPage;
